import { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { BASE_URL, HEADERS } from "../../../utils/constant";

const AddExpenseModal = ({
  show,
  handleClose,
  technicianId,
  onAddExpense,
  expenseData,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const [formData, setFormData] = useState({
    from_field: "",
    ticket: "",
    ticket: "",
    to: "",
    amount: "",
    description: "",
    expense_type: "",
  });

  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState(null);

  const role = localStorage.getItem("role");

  const [expense_types, setExpense_types] = useState([]);
  const [selectedExpense_types, setSelectedExpense_types] = useState(
    formData.expense_type,
  );
  const [selectedExpense_typesId, setSelectedExpense_typesId] = useState("");

  useEffect(() => {
    if (expenseData) {
      setValue("from_field", expenseData.from_field || "");
      setValue("to", expenseData.to || "");
      setValue("amount", expenseData.amount || "");
      setValue("description", expenseData.description || "");
      setValue("expense_date", expenseData.expense_date || "");

      if (role !== "sales" && expenseData.ticket) {
        const ticketId = expenseData.ticket.id;
        setValue("ticket", ticketId);
        setFormData((prev) => ({ ...prev, ticket: ticketId }));
      }

      const expenseTypeName = expenseData.expense_type || "";
      const selectedExpenseType = expense_types.find(
        (type) => type.name === expenseTypeName,
      );
      setSelectedExpense_typesId(selectedExpenseType?.id || "");
      setSelectedExpense_types(expenseTypeName);
      setValue("expense_type", selectedExpenseType?.id || "");

      setFile(null);
    }
  }, [expenseData, expense_types, setValue, role]);

  const handleSourceChange = (e) => {
    const selectedId = e.target.value;
    const selectedSourceName = expense_types.find(
      (expense_type) => expense_type.id === parseInt(selectedId),
    )?.name;

    setSelectedExpense_typesId(selectedId);
    setSelectedExpense_types(selectedSourceName || "");
    setValue("expense_type", selectedId);
  };

  const handleAddExpense = async (data) => {
    setLoading(true);
    try {
      const formDataToSubmit = new FormData();
      formDataToSubmit.append("technician", technicianId);
      formDataToSubmit.append("from_field", data.from_field);
      formDataToSubmit.append("to", data.to);
      formDataToSubmit.append("amount", parseFloat(data.amount));
      formDataToSubmit.append("description", data.description);
      formDataToSubmit.append("expense_type", selectedExpense_types || "");
      formDataToSubmit.append("ticket", role === "sales" ? "" : data.ticket);
      formDataToSubmit.append("expense_date", data.expense_date);
      if (file) {
        formDataToSubmit.append("image", file);
      }

      if (expenseData) {
        await axios.put(
          `${BASE_URL}/api/expenses/${expenseData.id}/`,
          formDataToSubmit,
          {
            headers: {
              ...HEADERS,
              "Content-Type": "multipart/form-data",
            },
          },
        );
      } else {
        await axios.post(`${BASE_URL}/api/expenses/`, formDataToSubmit, {
          headers: {
            ...HEADERS,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      toast.success(
        `Expense ${expenseData ? "updated" : "added"} successfully`,
      );
      handleClose();
      onAddExpense();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Something went wrong";

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSources = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/expense-type/`, {
          headers: HEADERS,
        });
        if (response.data) {
          setExpense_types(response.data);
        }
      } catch (error) {
        console.error("Error fetching expense_types:", error);
      }
    };
    fetchSources();
  }, []);

  const renderFilePreview = () => {
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      const isImage = file.type.startsWith("image/");
      const isPdf = file.type === "application/pdf";
      return (
        <div className="mt-2">
          {isImage ? (
            <img
              src={fileUrl}
              alt="Preview"
              style={{ width: "100px", height: "100px", objectFit: "cover" }}
            />
          ) : isPdf ? (
            <div>
              <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                View PDF
              </a>
            </div>
          ) : (
            <div>Unsupported file type</div>
          )}
        </div>
      );
    }
    if (expenseData?.image) {
      return (
        <div className="mt-2">
          <a href={expenseData.image} target="_blank" rel="noopener noreferrer">
            View Current File
          </a>
        </div>
      );
    }
    return null;
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Form noValidate onSubmit={handleSubmit(handleAddExpense)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {expenseData ? "Edit Expense" : "Add Expense"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            {role !== "sales" && (
              <Form.Group>
                <Form.Label htmlFor="ticket">Ticket ID*</Form.Label>
                <Form.Control
                  type="text"
                  {...register("ticket", {
                    required: "Ticket is required",
                  })}
                  placeholder="Enter ticket id"
                />
                {errors.ticket && (
                  <div className="text-danger">{errors.ticket.message}</div>
                )}
              </Form.Group>
            )}
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group controlId="expense_type">
                <Form.Label>Expense Type*</Form.Label>
                <div
                  className="input-group"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <>
                    <Form.Control
                      as="select"
                      name="expense_type"
                      {...register("expense_type", {
                        required: "Expense Type is required",
                      })}
                      value={selectedExpense_typesId}
                      onChange={handleSourceChange}
                      isInvalid={!!errors.expense_type}
                    >
                      <option value="">Select Expense Type</option>
                      {expense_types.length > 0 ? (
                        expense_types.map((expense_type) => (
                          <option key={expense_type.id} value={expense_type.id}>
                            {expense_type.name}
                          </option>
                        ))
                      ) : (
                        <option disabled>No Expense Type Available</option>
                      )}
                    </Form.Control>
                  </>
                </div>
                {errors.expense_type && (
                  <div className="text-danger">
                    {errors.expense_type.message}
                  </div>
                )}
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Expense Date*</Form.Label>
                <Form.Control
                  type="date"
                  {...register("expense_date", {
                    required: "Expense date is required",
                  })}
                />
                {errors.expense_date && (
                  <div className="text-danger">
                    {errors.expense_date.message}
                  </div>
                )}
              </Form.Group>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>From*</Form.Label>
                <Form.Control
                  type="text"
                  {...register("from_field", {
                    required: "Starting location is required",
                  })}
                  placeholder="Enter starting location"
                />
                {errors.from_field && (
                  <div className="text-danger">{errors.from_field.message}</div>
                )}
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>To*</Form.Label>
                <Form.Control
                  type="text"
                  {...register("to", { required: "Destination is required" })}
                  placeholder="Enter destination"
                />
                {errors.to && (
                  <div className="text-danger">{errors.to.message}</div>
                )}
              </Form.Group>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Amount* (₹)</Form.Label>
                <Form.Control
                  type="number"
                  {...register("amount", { required: "Amount is required" })}
                  placeholder="Enter amount spent"
                />
                {errors.amount && (
                  <div className="text-danger">{errors.amount.message}</div>
                )}
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Description*</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={1}
                  {...register("description", {
                    required: "Description is required",
                  })}
                  placeholder="Enter description"
                />
                {errors.description && (
                  <div className="text-danger">
                    {errors.description.message}
                  </div>
                )}
              </Form.Group>
            </div>
          </div>
          <div className="mb-3">
            <Form.Group>
              <Form.Label>Upload File (Optional)</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </Form.Group>
            {renderFilePreview()}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Submit"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddExpenseModal;
